/**
 * TargetPicker.tsx — WEB.4.UI.6
 *
 * Shown when a card with target=OpponentChosen is played.
 * Presents 3 opponent panels (excludes current seat) for victim selection.
 *
 * Flow:
 *   playCard (CardDetailModal) detects OpponentChosen
 *   → store routes to openTargetPicker (stores targetPickerCard, closes CDM)
 *   → this modal appears
 *   → player clicks a valid panel → confirmTarget(victimSeat)
 *   → effects run, card consumed, picker closes
 *   OR cancel → closeTargetPicker (card stays in hand)
 *
 * VISIBLE WHEN: targetPickerCard !== null
 * Keyboard: Esc → cancel, Enter → confirm if selected.
 *
 * Adder 1: Risk preview — floating tooltip on hover (post-resolution delta).
 * Adder 2: Edu fact ribbon — card.educationalFact if non-empty / non-TODO.
 * Adder 3: Selected-panel amberPulse (CSS only).
 */

import type { CardDef, CardEffect } from "@gdg/shared";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import tilePositionsData from "../../../../../data/board/tile-positions.json";
import { FACILITIES } from "../../store/facilities.js";
import { getCardById, useGameStore } from "../../store/game-store.js";
import type { SeatIndex } from "../../store/game-store.js";
import styles from "./TargetPicker.module.css";

// ---------------------------------------------------------------------------
// Bug #3 — category-filtered facility counting helpers
// ---------------------------------------------------------------------------

/** Match a facilityId against a single categoryFilter token (case-insensitive).
 *  Token may be FacilityType ("Coal") or FacilityCategory ("Fossil"). */
function _facilityMatchesToken(facilityId: string, token: string): boolean {
  const def = FACILITIES.find((f) => f.id === facilityId);
  if (def === undefined) return false;
  const t = token.toLowerCase();
  return def.id.toLowerCase() === t || def.category.toLowerCase() === t;
}

/** True when facilityId matches ANY token in filter (or filter empty/absent). */
function _facilityMatchesCategoryFilter(
  facilityId: string | undefined,
  filter: ReadonlyArray<string> | undefined,
): boolean {
  if (facilityId === undefined) return false;
  if (filter === undefined || filter.length === 0) return true;
  return filter.some((tok) => _facilityMatchesToken(facilityId, tok));
}

// ---------------------------------------------------------------------------
// Bug #4: artwork path resolver (mirrors CardDetailModal.resolveArtPath)
// ---------------------------------------------------------------------------

function resolveArtPath(artwork: string | null | undefined, cardType: string): string | null {
  if (!artwork) return null;
  const raw = artwork.trim();
  if (!raw) return null;
  if (/^[A-Z][A-Za-z0-9_]+$/.test(raw)) return `/images/cards/${raw}.jpg`;
  if (raw.startsWith("mitigate/") || raw.startsWith("policy/")) {
    return `/images/cards/source/${raw.replace(/\.jpg$/i, "")}.jpg`;
  }
  const policyDash = raw.match(/^policy-(\d+)(?:\.jpg)?$/i);
  if (policyDash) return `/images/cards/source/policy/${policyDash[1]}.jpg`;
  const stripped = raw.replace(/\.jpg$/i, "");
  const folder = cardType === "Policy" ? "policy" : cardType === "Mitigate" ? "mitigate" : "crisis";
  return `/images/cards/source/${folder}/${stripped}.jpg`;
}

// ---------------------------------------------------------------------------
// Tile lookup (shared with TileStage/BuildMenu pattern)
// ---------------------------------------------------------------------------

type TileEntry = { index: number; type: string; x: number; y: number };
const _tileTypeByIndex: Record<number, string> = Object.fromEntries(
  (tilePositionsData.tiles as TileEntry[]).map((t) => [t.index, t.type]),
);

// ---------------------------------------------------------------------------
// Vulnerability computation helpers
// ---------------------------------------------------------------------------

type FlagVariant = "good" | "partial" | "bad";

interface VulnerabilityResult {
  disabled: boolean;
  flagText: string;
  flagVariant: FlagVariant;
  riskText: string | null;
}

/**
 * Compute per-panel vulnerability flag from the card's OpponentChosen effects.
 * Pure function of card effects + current game state values.
 *
 * Bug #3: Destroy/Decommission with categoryFilter now use `filteredFacilityCount`
 * (count of facilities matching the filter) instead of all facilities.
 */
function computeVulnerability(
  effects: ReadonlyArray<CardEffect>,
  targetEnergy: number,
  targetTokens: number,
  targetFacilityCount: number,
  targetHandSize: number,
  currentEnergy: number,
  /** Codex R2 HIGH 5: Option C tie-breaking. True when this seat is in the
   *  set of opponents tied for max pollution (FacilityTransfer / superlative
   *  predicates). False = NOT in the tied-max-set (disabled). */
  isInMaxPollutionSet = true,
  /** True when ALL opponents tied at 0 (no one is "most polluting"). */
  allTiedAtZero = false,
  /** Bug #3: pre-computed count of facilities matching the effect's categoryFilter.
   *  Equals targetFacilityCount when no filter (or filter is empty). */
  filteredFacilityCount = -1,
): VulnerabilityResult {
  for (const eff of effects) {
    if (eff.target !== "OpponentChosen") continue;

    switch (eff.kind) {
      case "FacilityTransfer": {
        // Smog_Emergency: only most-polluting opponents are valid targets.
        if (!isInMaxPollutionSet) {
          return {
            disabled: true,
            flagText: "⚠ NOT MOST POLLUTING",
            flagVariant: "bad",
            riskText: null,
          };
        }
        if (targetFacilityCount === 0) {
          return {
            disabled: true,
            flagText: "⚠ NO FACILITIES TO TRANSFER",
            flagVariant: "bad",
            riskText: null,
          };
        }
        const labelText = allTiedAtZero
          ? `✓ TIED AT MAX (0) · ${targetFacilityCount} FACILITIES`
          : `✓ MOST POLLUTING · ${targetFacilityCount} FACILITIES`;
        return {
          disabled: false,
          flagText: labelText,
          flagVariant: "good",
          riskText: `Take 1 of ${targetFacilityCount} @ half buildCost · TilePicker WEB.4.UI.7`,
        };
      }

      case "StealEnergy": {
        const amount = typeof eff.valueInt === "number" ? eff.valueInt : 0;
        if (targetEnergy === 0) {
          return {
            disabled: true,
            flagText: "⚠ NO ENERGY TO STEAL",
            flagVariant: "bad",
            riskText: null,
          };
        }
        if (targetEnergy < amount) {
          return {
            disabled: false,
            flagText: `⚠ PARTIAL · ONLY ${targetEnergy} ENERGY`,
            flagVariant: "partial",
            riskText: `Steal ${targetEnergy}/${amount} · ⚡${targetEnergy}→⚡0 · You ⚡${currentEnergy}→⚡${currentEnergy + targetEnergy}`,
          };
        }
        return {
          disabled: false,
          flagText: `✓ FULL STEAL · ${targetEnergy} ENERGY`,
          flagVariant: "good",
          riskText: `Steal ${amount}/${amount} · ⚡${targetEnergy}→⚡${targetEnergy - amount} · You ⚡${currentEnergy}→⚡${currentEnergy + amount}`,
        };
      }

      case "Destroy":
      case "Decommission": {
        // Bug #3: use filtered count when categoryFilter is present.
        const catFilter = eff.categoryFilter;
        const hasFilter = catFilter !== undefined && catFilter.length > 0;
        // filteredFacilityCount is passed in when there's a category filter;
        // fall back to targetFacilityCount when unfiltered.
        const effectiveCount =
          hasFilter && filteredFacilityCount >= 0 ? filteredFacilityCount : targetFacilityCount;
        const filterLabel = hasFilter ? ` ${catFilter?.[0]?.toUpperCase() ?? ""}` : "";
        if (effectiveCount === 0) {
          return {
            disabled: true,
            flagText: hasFilter
              ? `⚠ NO${filterLabel} FACILITIES TO TARGET`
              : "⚠ NO FACILITIES TO DESTROY",
            flagVariant: "bad",
            riskText: null,
          };
        }
        return {
          disabled: false,
          flagText: `✓ ${effectiveCount}${filterLabel} FACILIT${effectiveCount === 1 ? "Y" : "IES"} — VALID`,
          flagVariant: "good",
          riskText: `Will destroy 1 of ${effectiveCount}${filterLabel.toLowerCase()} facilities`,
        };
      }

      case "StealCard": {
        if (targetHandSize === 0) {
          return {
            disabled: true,
            flagText: "⚠ NO CARDS IN HAND",
            flagVariant: "bad",
            riskText: null,
          };
        }
        return {
          disabled: false,
          flagText: `✓ ${targetHandSize} CARD${targetHandSize > 1 ? "S" : ""} IN HAND`,
          flagVariant: "good",
          riskText: `Will steal 1 of ${targetHandSize} cards`,
        };
      }

      case "TokenDelta": {
        const delta = typeof eff.valueInt === "number" ? eff.valueInt : 0;
        if (delta < 0 && targetTokens === 0) {
          return {
            disabled: true,
            flagText: "⚠ NO TOKENS — CANNOT DRAIN",
            flagVariant: "bad",
            riskText: null,
          };
        }
        return {
          disabled: false,
          flagText: `✓ ${Math.max(0, targetTokens + delta)} TOKENS AFTER`,
          flagVariant: "good",
          riskText: `Tokens ${targetTokens} → ${Math.max(0, targetTokens + delta)}`,
        };
      }

      case "PollutionDelta": {
        const delta = typeof eff.valueInt === "number" ? eff.valueInt : 0;
        return {
          disabled: false,
          flagText: `✓ +${delta} POLLUTION`,
          flagVariant: "good",
          riskText: `Pollution +${delta}`,
        };
      }

      case "DiceOutcome":
        return {
          disabled: false,
          flagText: "✓ VALID TARGET",
          flagVariant: "good",
          riskText: "Dice outcome resolves",
        };

      default:
        return { disabled: false, flagText: "✓ VALID", flagVariant: "good", riskText: null };
    }
  }

  // No OpponentChosen effect found
  return { disabled: false, flagText: "✓ VALID", flagVariant: "good", riskText: null };
}

// ---------------------------------------------------------------------------
// Effect summary (human-readable description for header)
// ---------------------------------------------------------------------------

function effectSummary(effects: ReadonlyArray<CardEffect>): string {
  const eff = effects.find((e) => e.target === "OpponentChosen");
  if (eff === undefined) return "Apply effect to chosen opponent.";
  // Bug #3: include categoryFilter in Destroy/Decommission descriptions.
  const catFilter = eff.categoryFilter;
  const filterStr =
    catFilter !== undefined && catFilter.length > 0
      ? ` (${catFilter.map((c) => c.toUpperCase()).join("/")}) `
      : " ";
  switch (eff.kind) {
    case "StealEnergy":
      return `Steal ${String(eff.valueInt ?? "?")} energy from chosen opponent.`;
    case "Destroy":
      return `Destroy 1${filterStr}facility owned by chosen opponent.`;
    case "Decommission":
      return `Decommission 1${filterStr}facility owned by chosen opponent.`;
    case "StealCard":
      return "Steal 1 card from chosen opponent's hand.";
    case "TokenDelta": {
      const v = typeof eff.valueInt === "number" ? eff.valueInt : 0;
      return v < 0
        ? `Remove ${String(Math.abs(v))} tokens from chosen opponent.`
        : `Give ${String(v)} tokens to chosen opponent.`;
    }
    case "PollutionDelta":
      return `Add ${String(eff.valueInt ?? "?")} pollution to chosen opponent.`;
    case "DiceOutcome":
      return "Roll dice — outcome applied to chosen opponent.";
    default:
      return "Apply effect to chosen opponent.";
  }
}

// ---------------------------------------------------------------------------
// Edu fact visibility guard (Adder 2)
// ---------------------------------------------------------------------------

/** Codex R2 HIGH 3: gate facts behind "Source:" citation marker —
 *  same locked rule as CardDetailModal. Prevents uncited claims shipping. */
function isEduFactVisible(fact: string | undefined): fact is string {
  if (!fact || fact.trim() === "" || fact.startsWith("(Authoring TODO")) return false;
  return fact.includes("Source:") || fact.includes("(Source");
}

/** Codex R2 HIGH 5: Option C tie-breaking for FacilityTransfer-style
 *  "most polluting" predicates. Returns ALL opponents tied at max value
 *  (NOT a single seat). Empty list when input empty. */
function findMaxBy<T extends number>(
  seats: ReadonlyArray<SeatIndex>,
  pickValue: (s: SeatIndex) => T,
): SeatIndex[] {
  if (seats.length === 0) return [];
  let maxVal = pickValue(seats[0] as SeatIndex);
  for (const seat of seats) {
    const v = pickValue(seat);
    if (v > maxVal) maxVal = v;
  }
  return seats.filter((s) => pickValue(s) === maxVal);
}

// ---------------------------------------------------------------------------
// Seat display helpers
// ---------------------------------------------------------------------------

const SEAT_NAMES = ["Player 1", "Player 2", "Player 3", "Player 4"] as const;
const COLOR_BAND_CLASSES = [
  styles.colorBandP1 ?? "",
  styles.colorBandP2 ?? "",
  styles.colorBandP3 ?? "",
  styles.colorBandP4 ?? "",
] as const;
const BODY_CLASSES = [
  styles.opBodyP1 ?? "",
  styles.opBodyP2 ?? "",
  styles.opBodyP3 ?? "",
  styles.opBodyP4 ?? "",
] as const;

function typeTagClass(cardType: string): string {
  if (cardType === "Policy") return styles.typeTagPolicy ?? "";
  if (cardType === "Crisis" || cardType === "Mitigate") return styles.typeTagCrisis ?? "";
  return styles.typeTagEvent ?? "";
}

// ---------------------------------------------------------------------------
// OpponentPanel sub-component
// ---------------------------------------------------------------------------

interface OpponentPanelProps {
  seat: SeatIndex;
  tokens: number;
  energy: number;
  pollution: number;
  facilityCount: number;
  /** Bug #3: facility count restricted to the Destroy/Decommission categoryFilter.
   *  -1 = no filter active (falls back to facilityCount). */
  filteredFacilityCount: number;
  handSize: number;
  tileLabel: string;
  currentEnergy: number;
  effects: ReadonlyArray<CardEffect>;
  isSelected: boolean;
  onSelect: (seat: SeatIndex) => void;
  isInMaxPollutionSet: boolean;
  allTiedAtZero: boolean;
}

function OpponentPanel({
  seat,
  tokens,
  energy,
  pollution,
  facilityCount,
  filteredFacilityCount,
  handSize,
  tileLabel,
  currentEnergy,
  effects,
  isSelected,
  onSelect,
  isInMaxPollutionSet,
  allTiedAtZero,
}: OpponentPanelProps): React.ReactElement {
  const [hovered, setHovered] = useState(false);
  const vuln = computeVulnerability(
    effects,
    energy,
    tokens,
    facilityCount,
    handSize,
    currentEnergy,
    isInMaxPollutionSet,
    allTiedAtZero,
    filteredFacilityCount,
  );

  const panelClass = [
    styles.opPanel,
    vuln.disabled ? styles.opPanelDisabled : "",
    isSelected && !vuln.disabled ? styles.opPanelSelected : "",
  ]
    .filter(Boolean)
    .join(" ");

  const bandClass = `${styles.colorBand} ${COLOR_BAND_CLASSES[seat]}`;
  const bodyClass = `${styles.opBody} ${BODY_CLASSES[seat]}`;
  const flagClass = [
    styles.flag,
    vuln.flagVariant === "good" ? styles.flagGood : "",
    vuln.flagVariant === "partial" ? styles.flagPartial : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick = useCallback(() => {
    if (!vuln.disabled) onSelect(seat);
  }, [vuln.disabled, onSelect, seat]);

  return (
    <button
      type="button"
      className={panelClass}
      data-testid={`op-panel-p${seat + 1}`}
      data-seat={`p${seat + 1}`}
      disabled={vuln.disabled}
      aria-pressed={isSelected}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Color band */}
      <div className={bandClass} data-testid={`op-band-p${seat + 1}`}>
        <span>{SEAT_NAMES[seat]}</span>
        <span className={styles.seatNum}>
          SEAT {seat + 1}
          {isSelected ? " ● SELECTED" : ""}
        </span>
      </div>

      {/* Body */}
      <div className={bodyClass}>
        {/* Stats */}
        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={`${styles.statIcon} ${styles.statIconTokens}`}>●</span>
            <span className={styles.statVal} data-testid={`op-tokens-p${seat + 1}`}>
              {String(tokens)}
            </span>
            <span className={styles.statLabel}>Tokens</span>
          </div>
          <div className={styles.stat}>
            <span className={`${styles.statIcon} ${styles.statIconEnergy}`}>⚡</span>
            <span className={styles.statVal} data-testid={`op-energy-p${seat + 1}`}>
              {String(energy)}
            </span>
            <span className={styles.statLabel}>Energy</span>
          </div>
          <div className={styles.stat}>
            <span className={`${styles.statIcon} ${styles.statIconPollution}`}>💨</span>
            <span className={styles.statVal} data-testid={`op-pollution-p${seat + 1}`}>
              {String(pollution)}
            </span>
            <span className={styles.statLabel}>Pollution</span>
          </div>
        </div>

        {/* Meta */}
        <div className={styles.meta}>
          <div className={styles.metaLine}>
            <span className={styles.metaLineStrong}>{String(facilityCount)}</span> facilities owned
          </div>
          <div className={styles.metaLine}>
            On: <span className={styles.metaLineStrong}>{tileLabel}</span>
          </div>
        </div>

        {/* Vulnerability flag */}
        {vuln.flagText !== "" && (
          <div className={flagClass} data-testid={`op-flag-p${seat + 1}`}>
            {vuln.flagText}
          </div>
        )}
      </div>

      {/* Risk preview — visible on hover, hidden on disabled (Adder 1) */}
      {hovered && !vuln.disabled && vuln.riskText !== null && (
        <div className={styles.riskPreview} data-testid={`op-risk-p${seat + 1}`}>
          {vuln.riskText}
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// TargetPicker — public component
// ---------------------------------------------------------------------------

export function TargetPicker(): React.ReactElement | null {
  const targetPickerCard = useGameStore((s) => s.targetPickerCard);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const tokens = useGameStore((s) => s.tokens);
  const energy = useGameStore((s) => s.energy);
  const pollution = useGameStore((s) => s.pollution);
  const hands = useGameStore((s) => s.hands);
  const playerTiles = useGameStore((s) => s.playerTiles);
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const ownedBy = useGameStore((s) => s.ownedBy);
  const closeTargetPicker = useGameStore((s) => s.closeTargetPicker);
  const confirmTarget = useGameStore((s) => s.confirmTarget);
  // Bug #3 fix: after a non-chained confirmTarget the picker closes but the user
  // was left staring at the board with "Leave Tile" still pending. Auto-request
  // scene leave so the turn advances immediately on Confirm Target click.
  // Chained cards (FacilityTransfer / Destroy / Decommission) open TilePicker
  // next — do NOT auto-leave in that case (TilePicker drives the flow).
  const requestLeaveScene = useGameStore((s) => s.requestLeaveScene);

  const [localSelected, setLocalSelected] = useState<SeatIndex | null>(null);

  // Codex R2 HIGH 4: reset selection when picker opens for a NEW card.
  // Component returns null (not unmount) between pickers, so React state persists.
  // Track instanceId to detect picker session changes.
  const cardInstanceId = targetPickerCard?.instanceId ?? null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: setter is stable; tracking cardInstanceId only
  useEffect(() => {
    setLocalSelected(null);
  }, [cardInstanceId]);

  // Keyboard: Esc → cancel, Enter → confirm, 1/2/3 → quick-select Nth opponent.
  // Codex R3 HIGH 2: quick-select MUST check eligibility (else can pick a
  // disabled opponent + Enter confirms an invalid target). Computed
  // separately below; this handler reads it via closure on each invocation.
  useEffect(() => {
    if (targetPickerCard === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeTargetPicker();
        return;
      }
      if (e.key === "Enter" && localSelected !== null) {
        // Defense-in-depth: don't confirm if card missing
        const s = useGameStore.getState();
        const cd = s.targetPickerCard === null ? undefined : getCardById(s.targetPickerCard.id);
        if (cd === undefined) return;
        // Bug #3: same chain-detection as handleConfirm
        const chainsViaTilePicker = cd.effects.some(
          (e) =>
            (e.kind === "FacilityTransfer" || e.kind === "Destroy" || e.kind === "Decommission") &&
            e.target === "OpponentChosen",
        );
        confirmTarget(localSelected);
        if (!chainsViaTilePicker) requestLeaveScene();
        return;
      }
      // 1/2/3 quick-select Nth ELIGIBLE opponent (re-read store at keypress)
      if (e.key === "1" || e.key === "2" || e.key === "3") {
        const idx = Number.parseInt(e.key, 10) - 1;
        const store = useGameStore.getState();
        const cd =
          store.targetPickerCard === null ? undefined : getCardById(store.targetPickerCard.id);
        if (cd === undefined) return;
        // Present seats only (< numPlayers) — never offer an absent seat as a target.
        const oppSeats = ([0, 1, 2, 3] as SeatIndex[]).filter(
          (s) => s !== store.currentSeat && s < store.numPlayers,
        );
        // Bug #3: compute both total and category-filtered facility counts per seat.
        const fcBySeat: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
        for (const tIdx of Object.keys(store.builtFacilities)) {
          const owner = store.ownedBy[Number(tIdx)];
          if (owner !== undefined) fcBySeat[owner] = (fcBySeat[owner] ?? 0) + 1;
        }
        const destroyEff = cd.effects.find(
          (eff) =>
            (eff.kind === "Destroy" || eff.kind === "Decommission") &&
            eff.target === "OpponentChosen",
        );
        const destroyCatFilter = destroyEff?.categoryFilter;
        const hasDestroyFilter = destroyCatFilter !== undefined && destroyCatFilter.length > 0;
        const filteredFcBySeat: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
        if (hasDestroyFilter) {
          for (const tIdx of Object.keys(store.builtFacilities)) {
            const facilityId = store.builtFacilities[Number(tIdx)];
            const owner = store.ownedBy[Number(tIdx)];
            if (
              owner !== undefined &&
              _facilityMatchesCategoryFilter(facilityId, destroyCatFilter)
            ) {
              filteredFcBySeat[owner] = (filteredFcBySeat[owner] ?? 0) + 1;
            }
          }
        }
        const hasFT = cd.effects.some(
          (eff) => eff.kind === "FacilityTransfer" && eff.target === "OpponentChosen",
        );
        const maxSet = hasFT
          ? new Set(findMaxBy(oppSeats, (s) => store.pollution[s] ?? 0))
          : new Set<SeatIndex>(oppSeats);
        const allZero = hasFT && oppSeats.every((s) => (store.pollution[s] ?? 0) === 0);
        const eligible = oppSeats.filter((seat) => {
          const v = computeVulnerability(
            cd.effects,
            store.energy[seat] ?? 0,
            store.tokens[seat] ?? 0,
            fcBySeat[seat] ?? 0,
            store.hands[seat]?.length ?? 0,
            store.energy[store.currentSeat] ?? 0,
            maxSet.has(seat),
            allZero,
            hasDestroyFilter ? (filteredFcBySeat[seat] ?? 0) : -1,
          );
          return !v.disabled;
        });
        const target = eligible[idx];
        if (target !== undefined) setLocalSelected(target);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [targetPickerCard, localSelected, closeTargetPicker, confirmTarget, requestLeaveScene]);

  if (targetPickerCard === null) return null;

  const cardDef = getCardById(targetPickerCard.id);
  if (cardDef === undefined) return null;

  // Opponent seats (all present seats except current). Absent seats (>= numPlayers)
  // keep INITIAL state and must never be offered as a target.
  const opponentSeats = ([0, 1, 2, 3] as SeatIndex[]).filter(
    (s) => s !== currentSeat && s < numPlayers,
  );

  // Facility count per seat: count ALL entries in ownedBy for each seat.
  const facilityCountBySeat: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
  for (const tileIdxStr of Object.keys(builtFacilities)) {
    const tileIdx = Number(tileIdxStr);
    const owner = ownedBy[tileIdx];
    if (owner !== undefined) {
      facilityCountBySeat[owner] = (facilityCountBySeat[owner] ?? 0) + 1;
    }
  }

  // Bug #3: category-filtered facility count per seat for Destroy/Decommission@OpponentChosen.
  const destroyEffect = cardDef.effects.find(
    (e) => (e.kind === "Destroy" || e.kind === "Decommission") && e.target === "OpponentChosen",
  );
  const destroyCatFilter = destroyEffect?.categoryFilter;
  const hasDestroyFilter = destroyCatFilter !== undefined && destroyCatFilter.length > 0;
  const filteredFacilityCountBySeat: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
  if (hasDestroyFilter) {
    for (const tileIdxStr of Object.keys(builtFacilities)) {
      const tileIdx = Number(tileIdxStr);
      const facilityId = builtFacilities[tileIdx];
      const owner = ownedBy[tileIdx];
      if (owner !== undefined && _facilityMatchesCategoryFilter(facilityId, destroyCatFilter)) {
        filteredFacilityCountBySeat[owner] = (filteredFacilityCountBySeat[owner] ?? 0) + 1;
      }
    }
  }

  // Codex R2 HIGH 5: Option C tie-breaking for FacilityTransfer cards.
  // Compute the SET of opponents tied at max pollution. Smog_Emergency caller
  // contract: only most-polluting opponents are valid targets; ties keep all.
  const hasFacilityTransfer = cardDef.effects.some(
    (e) => e.kind === "FacilityTransfer" && e.target === "OpponentChosen",
  );
  const maxPollutionSet = hasFacilityTransfer
    ? new Set(findMaxBy(opponentSeats, (s) => pollution[s] ?? 0))
    : new Set<SeatIndex>(opponentSeats); // non-FT cards: all opponents in set
  const allTiedAtZero =
    hasFacilityTransfer && opponentSeats.every((s) => (pollution[s] ?? 0) === 0);

  // Eligible opponents (non-disabled) — use filtered count for Destroy/Decommission.
  const eligibleCount = opponentSeats.filter((seat) => {
    const vuln = computeVulnerability(
      cardDef.effects,
      energy[seat] ?? 0,
      tokens[seat] ?? 0,
      facilityCountBySeat[seat] ?? 0,
      hands[seat]?.length ?? 0,
      energy[currentSeat] ?? 0,
      maxPollutionSet.has(seat),
      allTiedAtZero,
      hasDestroyFilter ? (filteredFacilityCountBySeat[seat] ?? 0) : -1,
    );
    return !vuln.disabled;
  }).length;
  const noValidTargets = eligibleCount === 0;

  // Tile label helper
  function getTileLabel(seat: SeatIndex): string {
    const tileIdx = playerTiles[seat] ?? 0;
    const type = _tileTypeByIndex[tileIdx] ?? "Unknown";
    return `Tile ${String(tileIdx).padStart(2, "0")} · ${type}`;
  }

  const isConfirmEnabled = localSelected !== null && !noValidTargets;
  const eduFact = (cardDef as CardDef & { educationalFact?: string }).educationalFact;
  const showEduFact = isEduFactVisible(eduFact);

  const handleSelect = (seat: SeatIndex) => {
    setLocalSelected((prev) => (prev === seat ? null : seat));
  };

  const handleConfirm = () => {
    if (localSelected === null) return;
    // Bug #3: detect chained cards — FacilityTransfer/Destroy/Decommission@OpponentChosen
    // chain into TilePicker; those should NOT auto-leave (TilePicker drives the turn).
    const chainsToTilePicker = cardDef.effects.some(
      (e) =>
        (e.kind === "FacilityTransfer" || e.kind === "Destroy" || e.kind === "Decommission") &&
        e.target === "OpponentChosen",
    );
    confirmTarget(localSelected);
    if (!chainsToTilePicker) {
      // Non-chained card: effects applied inline — advance turn immediately.
      requestLeaveScene();
    }
  };

  const promptClass = noValidTargets ? `${styles.prompt} ${styles.promptNoTarget}` : styles.prompt;

  return (
    <div className={styles.overlay} data-testid="target-picker-overlay">
      <div className={styles.panel} data-testid="target-picker-panel">
        {/* Codex R2 MED 1: NO X close button — Cancel button + Esc suffice.
            Locked rule from earlier sub-phases. */}

        {/* HEADER (row 1) */}
        <div className={styles.header} data-testid="target-picker-header">
          <div className={styles.thumb}>
            {/* Bug #4: use resolveArtPath so numeric/subfolder artwork keys work.
                thumbImg uses object-fit:contain so portrait card is never clipped. */}
            {resolveArtPath(cardDef.artwork, cardDef.cardType) !== null && (
              <img
                className={styles.thumbImg}
                src={resolveArtPath(cardDef.artwork, cardDef.cardType) as string}
                alt={`${cardDef.cardName} card art`}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            <div className={styles.thumbPlaceholder}>[{cardDef.cardName}]</div>
          </div>

          <div className={styles.titleBlock}>
            <div className={styles.cardName}>
              {cardDef.cardName}
              <span className={styles.cardVerb}> — Choose Target</span>
            </div>
            <div className={styles.effectSummary}>{effectSummary(cardDef.effects)}</div>
          </div>

          <div
            className={`${styles.typeTag} ${typeTagClass(cardDef.cardType)}`}
            data-testid="target-picker-type-tag"
          >
            {cardDef.cardType.toUpperCase()}
          </div>
        </div>

        {/* PROMPT (row 2) — Bug #3: name the filter when present */}
        <div className={promptClass} data-testid="target-picker-prompt">
          {noValidTargets
            ? hasDestroyFilter
              ? `⚠ NO ${destroyCatFilter?.[0]?.toUpperCase() ?? ""} FACILITIES — CANCEL TO RETURN CARD TO HAND`
              : "⚠ NO VALID TARGETS — CANCEL TO RETURN CARD TO HAND"
            : hasDestroyFilter
              ? `CHOOSE 1 OPPONENT WITH ${destroyCatFilter?.[0]?.toUpperCase() ?? ""} FACILIT${eligibleCount === 1 ? "Y" : "IES"}`
              : "CHOOSE 1 OPPONENT"}
        </div>

        {/* BODY (row 3): 3 opponent panels */}
        <div className={styles.body} data-testid="target-picker-body">
          {opponentSeats.map((seat) => (
            <OpponentPanel
              key={seat}
              seat={seat}
              tokens={tokens[seat] ?? 0}
              energy={energy[seat] ?? 0}
              pollution={pollution[seat] ?? 0}
              facilityCount={facilityCountBySeat[seat] ?? 0}
              filteredFacilityCount={
                hasDestroyFilter ? (filteredFacilityCountBySeat[seat] ?? 0) : -1
              }
              handSize={hands[seat]?.length ?? 0}
              tileLabel={getTileLabel(seat)}
              currentEnergy={energy[currentSeat] ?? 0}
              effects={cardDef.effects}
              isInMaxPollutionSet={maxPollutionSet.has(seat)}
              allTiedAtZero={allTiedAtZero}
              isSelected={localSelected === seat}
              onSelect={handleSelect}
            />
          ))}
        </div>

        {/* EDU FACT RIBBON — Adder 2 */}
        {showEduFact && (
          <div className={styles.eduRibbon} data-testid="target-picker-edu-ribbon">
            <span className={styles.eduRibbonLabel}>Real-World Anchor</span>
            {eduFact}
          </div>
        )}

        {/* DIVIDER */}
        <div className={styles.divider} />

        {/* FOOTER (row 4) */}
        <div className={styles.footer} data-testid="target-picker-footer">
          <button
            type="button"
            className={
              noValidTargets ? `${styles.cancelBtn} ${styles.cancelBtnHighlight}` : styles.cancelBtn
            }
            onClick={closeTargetPicker}
            data-testid="target-picker-cancel"
          >
            ← Cancel
          </button>

          <div
            className={
              noValidTargets ? `${styles.footerHint} ${styles.footerHintWarn}` : styles.footerHint
            }
            data-testid="target-picker-hint"
          >
            {noValidTargets
              ? "No valid targets — card returns to hand."
              : localSelected !== null
                ? `Targeting Player ${localSelected + 1} — Confirm to apply effect.`
                : "Click a player panel to choose your target."}
          </div>

          <button
            type="button"
            className={
              isConfirmEnabled
                ? styles.confirmBtn
                : `${styles.confirmBtn} ${styles.confirmBtnDisabled}`
            }
            onClick={handleConfirm}
            disabled={!isConfirmEnabled}
            data-testid="target-picker-confirm"
          >
            Confirm Target →
          </button>
        </div>
      </div>
    </div>
  );
}
